import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronDown } from "lucide-react";

/**
 * MultiSelect: tag-style multi-select with suggestions from `options`.
 * Users can also type free-form values and press Enter/comma.
 *
 * Props:
 *  - value: string[]
 *  - onChange: (arr: string[]) => void
 *  - options: string[]
 *  - placeholder: string
 *  - testId: string
 *  - disabled: boolean
 */
export default function MultiSelect({ value = [], onChange, options = [], placeholder = "Type or select…", testId, disabled = false }) {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const arr = Array.isArray(value) ? value : [];

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const lowered = new Set(arr.map((s) => s.toLowerCase()));
    return options
      .filter((o) => !lowered.has(o.toLowerCase()))
      .filter((o) => o.toLowerCase().includes(input.toLowerCase()))
      .slice(0, 12);
  }, [options, input, arr]);

  const addTag = (t) => {
    const clean = (t || "").trim();
    if (!clean) return;
    if (arr.some((x) => x.toLowerCase() === clean.toLowerCase())) return;
    onChange([...arr, clean]);
    setInput("");
  };
  const removeTag = (t) => onChange(arr.filter((x) => x !== t));

  const onKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === ",") && input.trim()) {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && !input && arr.length) {
      removeTag(arr[arr.length - 1]);
    }
  };

  return (
    <div ref={wrap} className="relative">
      <div
        data-testid={testId}
        onClick={() => !disabled && setOpen(true)}
        className={`flex flex-wrap items-center gap-1.5 min-h-[38px] px-2 py-1.5 border rounded-md bg-white transition-colors ${
          disabled ? "opacity-60 cursor-not-allowed" : "border-slate-200 hover:border-slate-300 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-100 cursor-text"
        }`}
      >
        {arr.map((t) => (
          <span key={t} data-testid={`${testId}-tag-${t}`} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-indigo-50 text-indigo-700 rounded">
            {t}
            {!disabled && (
              <button type="button" onClick={(e) => { e.stopPropagation(); removeTag(t); }} className="hover:text-indigo-900" data-testid={`${testId}-remove-${t}`}>
                <X className="w-3 h-3" />
              </button>
            )}
          </span>
        ))}
        <input
          data-testid={testId ? `${testId}-input` : undefined}
          disabled={disabled}
          value={input}
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder={arr.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[100px] text-sm outline-none bg-transparent py-1"
        />
        {!disabled && <ChevronDown className="w-4 h-4 text-slate-400" />}
      </div>

      {open && !disabled && (filtered.length > 0 || input.trim()) && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-52 overflow-auto">
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              data-testid={testId ? `${testId}-option-${o}` : undefined}
              onClick={() => addTag(o)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 hover:text-indigo-700"
            >
              {o}
            </button>
          ))}
          {input.trim() && !filtered.some((o) => o.toLowerCase() === input.toLowerCase()) && (
            <button
              type="button"
              data-testid={testId ? `${testId}-create` : undefined}
              onClick={() => addTag(input)}
              className="w-full text-left px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 border-t border-slate-100"
            >
              + Add "{input.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
